import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import PublicPromptCard from './PublicPromptCard';
import { API_ENDPOINTS } from '@/constants';
import { useLanguage } from '@/contexts';

interface PublicMainContentProps {
    selectedCategory: string;
    onCategoriesClick: () => void;
}

interface Prompt {
    id: string;
    title: string;
    title_es?: string;
    description: string;
    description_es?: string;
    votes: number;
    total_views: number;
    author_name?: string;
    author_profile_picture?: string;
    categories?: string[];
}

const PublicMainContent: React.FC<PublicMainContentProps> = ({
    selectedCategory,
    onCategoriesClick,
}) => {
    const [prompts, setPrompts] = useState<Prompt[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const navigate = useNavigate();
    const { t, i18n } = useLanguage();

    useEffect(() => {
        const fetchPrompts = async () => {
            try {
                setLoading(true);
                setError(null);

                // Determine endpoint based on selected category
                const endpoint =
                    selectedCategory === 'All'
                        ? API_ENDPOINTS.PROMPTS.LIST
                        : API_ENDPOINTS.PROMPTS.CATEGORY(selectedCategory);

                // Build full URL (using VITE_API_URL in production, relative path in dev)
                const isDevelopment = import.meta.env.DEV;
                const baseUrl = isDevelopment
                    ? ''
                    : import.meta.env.VITE_API_URL || 'https://api-dev.promptshelf.ai';

                const userLang = i18n.language;
                const separator = endpoint.includes('?') ? '&' : '?';
                const url = `${baseUrl}${endpoint}${separator}lang=${userLang}`;

                // Use regular fetch (not authenticatedFetch) - this is a public endpoint
                const response = await fetch(url, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-User-Language': userLang,
                    },
                    credentials: 'include',
                });

                if (!response.ok) {
                    throw new Error(`Failed to fetch prompts: ${response.statusText} (${response.status})`);
                }

                const data = await response.json();

                // Limit to first 20 prompts as per requirements
                const limitedPrompts = Array.isArray(data) ? data.slice(0, 20) : [];
                setPrompts(limitedPrompts);
                setError(null);
            } catch (err) {
                const errorMessage = err instanceof Error ? err.message : t('failedLoadPrompts') || 'Failed to load prompts. Please try again.';
                setError(errorMessage);
                console.error('Error fetching public prompts:', err);
            } finally {
                setLoading(false);
            }
        };

        fetchPrompts();
    }, [selectedCategory, i18n.language, t]);

    return (
        <div className='flex flex-col w-full h-full'>

            <header className="shrink-0 py-6 lg:py-8 px-4 lg:px-8">
                <div className="flex justify-between items-center mb-8 lg:mb-12 max-w-7xl mx-auto">
                    <div className="flex items-baseline gap-2 md:gap-4 lg:gap-6">
                        <h1 className="text-xl md:text-2xl lg:text-3xl font-bold text-gray-900 tracking-tight text-left">
                            {t("discover")}
                        </h1>
                        <p className="text-sm md:text-base lg:text-lg text-gray-600 font-medium hidden md:block text-left px-4">
                            {t("mainContentTitle")}
                        </p>
                    </div>
                    <div className="flex items-center gap-2 md:gap-3">

                        <button
                            className="flex items-center gap-2 px-3 md:px-4 py-2 md:py-2.5 bg-brand-dark-blue hover:bg-brand-electric-blue text-white rounded-lg lg:hidden font-medium transition-colors duration-200 text-sm md:text-base"
                            onClick={onCategoriesClick}
                        >
                            {t('categories')} <span className="text-xs md:text-sm">▼</span>
                        </button>


                        <button
                            onClick={() => navigate('/auth/login')}
                            className="px-4 md:px-5 py-2 md:py-2.5 bg-brand-electric-blue text-white rounded-lg hover:bg-brand-dark-blue font-medium transition-colors shadow-sm text-sm md:text-base"
                        >
                            {t('login')}
                        </button>


                        <button
                            onClick={() => navigate('/auth/signup')}
                            className="px-4 md:px-5 py-2 md:py-2.5 bg-white border-2 border-brand-electric-blue text-brand-electric-blue rounded-lg hover:bg-brand-light-gray font-medium transition-colors text-sm md:text-base hidden sm:block"
                        >
                            {t('signUp')}
                        </button>
                    </div>
                </div>
            </header>


            <div className="flex-1 overflow-y-auto px-4 lg:px-8">
                <div className="max-w-7xl mx-auto space-y-4">

                    {error && (
                        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative my-4" role="alert">
                            <strong className="font-bold">Error: </strong>
                            <span className="block sm:inline">{error}</span>
                        </div>
                    )}


                    {loading ? (
                        <div className="flex justify-center items-center py-10">
                            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-gray-400"></div>
                            <span className="ml-3 text-gray-600">
                                {t('loadingPrompts')}
                            </span>
                        </div>
                    ) : prompts && prompts.length > 0 ? (
                        <>

                            {prompts.map((prompt) => (
                                <PublicPromptCard key={prompt.id} prompt={prompt} />
                            ))}


                            <div className="text-center py-6 text-gray-500 text-sm bg-gray-50 rounded-lg border border-gray-200">
                                <p className="mb-2">
                                    📋 {t('showing')} {prompts.length} {t('ofAvailablePrompts') || 'of available prompts'}
                                </p>
                                <p className="text-xs">
                                    {t('loginToAccessAll') || 'Login to access all prompts and create your own!'}
                                </p>
                            </div>
                        </>
                    ) : (

                        <div className="text-center py-10 text-gray-500">
                            {selectedCategory && selectedCategory !== 'All'
                                ? `${t("noPromptFound")} ${t("category")} ${selectedCategory}.`
                                : t("noPromptsFound")}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default PublicMainContent;
